import React, { useEffect, useState, useRef } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Drawer,
  Descriptions,
  Anchor,
  Tag,
  notification,
  Modal,
  Input,
  Form,
  DatePicker,
} from "antd";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Table } from "ant-table-extensions";
import http from "../../../../helpers/http";
import helper from "../../../../helpers/helper";
import config from "../../../../config/config";
import _ from "lodash";
import "./style.css";
import moment from "moment";

function DiwaliBonanza2026DailyWinnerGenerate() {
  const { confirm } = Modal;

  // ─── State ────────────────────────────────────────────────────────────────

  const [loading, setloading] = useState(false);
  const [btnloading, setbtnloading] = useState(false);
  const [form] = Form.useForm();
  const [formError, setFormError] = useState({});

  // Selected date string "YYYY-MM-DD" from DatePicker
  const [selectedDate, setSelectedDate] = useState(null);

  // Whether a batch already exists for selectedDate — controls Generate button
  const [batchExists, setBatchExists] = useState(false);

  const [batch, setBatch] = useState(null);
  const [voucherStats, setVoucherStats] = useState({
    totalVouchers: 0,
    usedVouchers: 0,
    remainingVouchers: 0,
  });
  
  const [voucherStatsLoading, setVoucherStatsLoading] = useState(false);

  // Winners list displayed in the table
  const [winners, setWinners] = useState([]);

  // Single winner loaded into Drawer
  const [drawerOpen, setdraweropen] = useState(false);
  const [drawerWinner, setDrawerWinner] = useState({});

  // Per-row loading — keyed by winner _id
  const [rejectLoading, setRejectLoading] = useState({});
  const [replacementLoading, setReplacementLoading] = useState({});
  const [verifyLoading, setVerifyLoading] = useState({});

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  // ─── Constants ────────────────────────────────────────────────────────────

  const moduleNamePural = "Dream Big Win Bigger 2026";
  const base_url =
    config.api_url + "admin/leads/diwali-bonanza-2026"; //without trailing slash

  const azure_object_url =
    config.azure_object_url + "public/uploads/diwali-bonanza-2026/";
  const { Link } = Anchor;

  const selectionTypeLabels = {
    UNIQUE_WITH_INVOICE: "Unique + Invoice",
    DUPLICATE_WITH_INVOICE: "Duplicate + Invoice",
    UNIQUE_WITHOUT_INVOICE: "Unique Without Invoice",
  };

  // ─── API helpers ──────────────────────────────────────────────────────────

  /**
   * Calls GET /daily-winners?date=... to load the current batch's winners.
   * If winners exist it means a batch already exists — sets batchExists=true
   * so the Generate button becomes disabled.
   * Calls GET /admin/leads/diwali-bonanza-2026/daily-winners
   */
  const loadBatch = async (date) => {
    if (!date) return;

    setloading(true);
    try {
      const res = await http.get(`${base_url}/daily-winners`, {
        params: { date },
      });

      if (res && res.data) {
        const batch = res.data.batch;
        const winners = res.data.winners || [];
    
        console.log(res);
        console.log(batch);
        console.log(winners);
    
        const formattedWinners = winners.map((winner) => ({
          ...winner,
          email: winner.entry?.email || "",
        }));
        
        setBatch(batch);
        setWinners(formattedWinners);
        setBatchExists(formattedWinners.length > 0);
      } else {
        setWinners([]);
        setBatchExists(false);
      }
    } catch (err) {
      notification["error"]({
        message:
          err?.response?.data?.message || "Failed to load batch winners.",
      });
      setWinners([]);
      setBatchExists(false);
    } finally {
      setloading(false);
    }
  };

  /**
   * Calls POST /daily-winners/generate with the selected date.
   * After success immediately reloads the batch so winners appear in the table
   * and the Generate button becomes disabled.
   * Calls POST /admin/leads/diwali-bonanza-2026/daily-winners/generate
   */
  const generateWinners = async () => {
    if (!selectedDate) {
      setFormError({ date: "Please select a date" });
      return;
    }

    setFormError({});
    setbtnloading(true);

    try {
      const result = await http.post(`${base_url}/daily-winners/generate`, {
        date: selectedDate,
      });

      if (result) {
        notification["success"]({
          message: "Daily winners generated successfully.",
        });
        // Immediately reload the batch — disables Generate button automatically
        await loadBatch(selectedDate);
      }
    } catch (err) {
      notification["error"]({
        message:
          err?.response?.data?.message || "Failed to generate daily winners.",
      });
    } finally {
      setbtnloading(false);
    }
  };

  /**
   * Loads full winner details and opens the Drawer.
   * Calls GET /admin/leads/diwali-bonanza-2026/daily-winners/:id
   */
  const loadWinner = async (id) => {
    try {
      const res = await http.get(`${base_url}/daily-winners/${id}`);
  
      console.log(res);
  
      if (res && res.data) {
        setDrawerWinner(res.data);
        setdraweropen(true);
      }
    } catch (err) {
      notification.error({
        message:
          err?.response?.data?.message || "Failed to load winner details.",
      });
    }
  };

  const loadVoucherStats = async () => {
    setVoucherStatsLoading(true);
  
    try {
      const res = await http.get(`${base_url}/vouchers/stats`);
  
      if (res && res.data) {
        setVoucherStats({
          totalVouchers: res.data.totalVouchers || 0,
          usedVouchers: res.data.usedVouchers || 0,
          remainingVouchers: res.data.remainingVouchers || 0,
        });
      }
    } catch (err) {
      notification.error({
        message:
          err?.response?.data?.message ||
          "Failed to load voucher statistics.",
      });
    } finally {
      setVoucherStatsLoading(false);
    }
  };

  useEffect(() => {
    loadVoucherStats();
  }, []);

  /**
   * Shows a confirmation modal then calls POST /daily-winners/:id/reject.
   * Reloads the current batch after success.
   * Calls POST /admin/leads/diwali-bonanza-2026/daily-winners/:id/reject
   */
  const rejectWinner = (id) => {
    confirm({
      title: "Are you sure you want to reject this winner?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",

      onOk: async () => {
        setRejectLoading((prev) => ({ ...prev, [id]: true }));
        try {
          const result = await http.post(
            `${base_url}/daily-winners/${id}/reject`
          );

          if (result) {
            notification["success"]({
              message: "Winner rejected successfully.",
            });
            // Reload current batch to reflect updated status — no page reload
            await loadBatch(selectedDate);
          }
        } catch (err) {
          notification["error"]({
            message:
              err?.response?.data?.message || "Failed to reject winner.",
          });
        } finally {
          setRejectLoading((prev) => ({ ...prev, [id]: false }));
        }
      },
      onCancel() { },
    });
  };

  const verifyWinner = (id) => {
    confirm({
      title: "Verify this winner?",
      icon: <ExclamationCircleOutlined />,
      okText: "Verify",
      cancelText: "Cancel",
  
      onOk: async () => {
        setVerifyLoading((prev) => ({
          ...prev,
          [id]: true,
        }));
  
        try {
          const result = await http.post(
            `${base_url}/daily-winners/${id}/verify`
          );
  
          if (result) {
            notification.success({
              message: "Winner verified successfully.",
            });
          
            // Update only the verified winner in the existing table
            setWinners((prevWinners) =>
              prevWinners.map((winner) =>
                winner._id === id
                  ? { ...winner, is_verified: true }
                  : winner
              )
            );
          }
        } catch (err) {
          notification.error({
            message:
              err?.response?.data?.message ||
              "Failed to verify winner.",
          });
        } finally {
          setVerifyLoading((prev) => ({
            ...prev,
            [id]: false,
          }));
        }
      },
    });
  };

  /**
   * Shows a confirmation modal then calls POST /daily-winners/:id/replacement.
   * Only available when winner is rejected and not verified.
   * Reloads the current batch after success.
   * Calls POST /admin/leads/diwali-bonanza-2026/daily-winners/:id/replacement
   */
  const replacementWinner = (id) => {
    confirm({
      title: "Generate replacement winner?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "Generate",
      okType: "primary",
      cancelText: "Cancel",

      onOk: async () => {
        setReplacementLoading((prev) => ({ ...prev, [id]: true }));
        try {
          const result = await http.post(
            `${base_url}/daily-winners/${id}/replacement`
          );

          if (result) {
            notification["success"]({
              message: "Replacement winner generated successfully.",
            });
            // Reload current batch to include newly generated replacement
            await loadBatch(selectedDate);
          }
        } catch (err) {
          notification["error"]({
            message:
              err?.response?.data?.message ||
              "Failed to generate replacement winner.",
          });
        } finally {
          setReplacementLoading((prev) => ({ ...prev, [id]: false }));
        }
      },
      onCancel() { },
    });
  };

  // ─── Date change handler ──────────────────────────────────────────────────

  /**
   * When tester changes the date, immediately call GET to check whether
   * a batch already exists. If it does, populate the table and disable
   * the Generate button without requiring any further user action.
   */
  const handleDateChange = async (date, datestring) => {
    setSelectedDate(datestring);
    setBatchExists(false);
    setWinners([]);
    setFormError({});

    if (datestring) {
      await loadBatch(datestring);
    }
  };

  // ─── Form submit handler ──────────────────────────────────────────────────

  // const handleSubmit = async (value) => {
  //   await generateWinners();
  // };
  const handleSubmit = () => {
    if (!selectedDate) {
      setFormError({ date: "Please select a date" });
      return;
    }
  
    confirm({
      title: "Are you sure you want to generate daily winners?",
      icon: <ExclamationCircleOutlined />,
      content: `Daily winners will be generated for ${moment(
        selectedDate,
        "YYYY-MM-DD"
      ).format("DD-MMM-YYYY")}. This action cannot be undone.`,
      okText: "Generate",
      okType: "primary",
      cancelText: "Cancel",
  
      onOk: async () => {
        await generateWinners();
      },
  
      onCancel() {},
    });
  };

  // ─── Drawer close ─────────────────────────────────────────────────────────

  const handleDrawerClose = () => {
    setDrawerWinner({});
    setdraweropen(false);
  };

  // ─── Column search helpers (copied from existing 2026 pages) ──────────────

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <div
          style={{
            padding: 8,
          }}
        >
          <Input
            autoFocus
            placeholder="Type text here"
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
              confirm({ closeDropdown: false });
            }}
            onPressEnter={() => {
              confirm();
            }}
            onBlur={() => {
              confirm();
            }}
            style={{
              marginBottom: 8,
              display: "block",
            }}
          ></Input>
          <Space>
            {/* <Button
           onClick={() => {
            clearFilters();
            setSelectedKeys(selectedKeys[0]);
            confirm();
           

          }}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button> */}
          </Space>
        </div>
      );
    },
    filterIcon: () => {
      return <SearchOutlined />;
    },
    onFilter: (value, record) => {
      return String(record[dataIndex] || "")
        .toLowerCase()
        .includes(value.toLowerCase());
    },
  });

  // ─── Table columns (same as DiwaliBonanza2026DailyWinners) ───────────────

  const columns = [
    {
      title: "Email Id",
      dataIndex: "email",
      // ...getColumnSearchProps("email"),
      key: "email",
      render: (text, record) => {
        return record.entry?.email || "NA";
      },
    },
    {
      title: "Selection Type",
      dataIndex: "selection_type",
      key: "selection_type",
      // ...getColumnSearchProps("selection_type"),
      render: (text, record) => {
        return (
          selectionTypeLabels[record.selection_type] ||
          record.selection_type ||
          "NA"
        );
      },
    },
    {
      title: "Generated Date",
      key: "generatedDate",
      render: (text, record) => {
        return record.createdAt
          ? helper.UserDate(record.createdAt, "-04:00")
          : "NA";
      },
    },
    {
      title: "Status",
      key: "status",
      render: (text, record) => {
        if (record.is_verified) {
          return <Tag color="green">Verified</Tag>;
        }
        if (record.is_rejected) {
          return <Tag color="red">Rejected</Tag>;
        }
        return <Tag color="orange">Pending</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => {
        const isVerified = record.is_verified === true;
        const isRejected = record.is_rejected === true;

        return (
          <Space size="middle">
            {/* View — always enabled; opens Drawer with full details */}
            <Button
              onClick={() => {
                loadWinner(record._id);
              }}
            >
              View
            </Button>

            {/* Reject — disabled when already verified or rejected */}
            <Button
              type="danger"
              disabled={isVerified || isRejected}
              loading={rejectLoading[record._id] || false}
              onClick={() => {
                rejectWinner(record._id);
              }}
            >
              Reject
            </Button>

            {/* Replacement — enabled ONLY when rejected and not verified */}
            <Button
              disabled={
                !isRejected ||
                isVerified ||
                record.replacement_generated
              }
              loading={replacementLoading[record._id] || false}
              onClick={() => {
                replacementWinner(record._id);
              }}
            >
              Replacement
            </Button>

            <Button
              type="primary"
              disabled={isVerified || isRejected}
              loading={verifyLoading[record._id] || false}
              onClick={() => verifyWinner(record._id)}
            >
              Verify
            </Button>
          </Space>
        );
      },
    },
  ];

  // ─── Export fields (same as DiwaliBonanza2026DailyWinners) ───────────────

  const fields: ITableExportFields = {
    selection_type: {
      header: "Selection Type",
      formatter: (_fieldValue, record) => {
        return (
          selectionTypeLabels[record.selection_type] ||
          record.selection_type ||
          "NA"
        );
      },
    },
    first_name: {
      header: "First Name",
      formatter: (_fieldValue, record) => {
        return record.entry?.first_name || "NA";
      },
    },
    last_name: {
      header: "Last Name",
      formatter: (_fieldValue, record) => {
        return record.entry?.last_name || "NA";
      },
    },

    phone: {
      header: "Contact Number",
      formatter: (_fieldValue, record) => {
        if (record.entry?.country_code && record.entry?.phone) {
          return `${record.entry.country_code} ${record.entry.phone}`;
        }

        if (record.entry?.phone) {
          return record.entry.phone;
        }

        return "NA";
      },
    },
    email: {
      header: "Email Id",
      formatter: (_fieldValue, record) => {
        return record.entry?.email || "NA";
      },
    },
    gender: {
      header: "Gender",
      formatter: (_fieldValue, record) => {
        return record.entry?.gender || "NA";
      },
    },
    date_of_birth: {
      header: "Date of Birth",
      formatter: (_fieldValue, record) => {
        return record.entry?.date_of_birth
          ? `="${moment(record.entry.date_of_birth).format("DD-MMM-YYYY")}"`
          : "NA";
      },
    },
    address: {
      header: "Address",
      formatter: (_fieldValue, record) => {
        return record.entry?.address || "NA";
      },
    },
    country: {
      header: "Country",
      formatter: (_fieldValue, record) => {
        return record.entry?.country || "NA";
      },
    },
    state: {
      header: "State",
      formatter: (_fieldValue, record) => {
        return record.entry?.state || "NA";
      },
    },

    city: {
      header: "City",
      formatter: (_fieldValue, record) => {
        return record.entry?.city
          ? _.replace(_.deburr(record.entry.city), /'/g, "")
          : "NA";
      },
    },
    zip: {
      header: "Zip Code",
      formatter: (_fieldValue, record) => {
        return record.entry?.zip || "NA";
      },
    },
    store: {
      header: "Which store did you buy this Laxmi product from?",
      formatter: (_fieldValue, record) => {
        return record.entry?.store || "NA";
      },
    },
    campaign_source: {
      header: "How did you get to know about this campaign?",
      formatter: (_fieldValue, record) => {
        return record.entry?.campaign_source || "NA";
      },
    },
    laxmi_fav_product: {
      header: "What is your favorite product from Laxmi?",
      formatter: (_fieldValue, record) => {
        return record.entry?.laxmi_fav_product || "NA";
      },
    },
    laxmi_brand_one_word: {
      header: "Can you Describe Laxmi Brand in one word?",
      formatter: (_fieldValue, record) => {
        return record.entry?.laxmi_brand_one_word || "NA";
      },
    },
    laxmi_ads_desc: {
      header: "Have you seen Laxmi ads before? if yes where?",
      formatter: (_fieldValue, record) => {
        return record.entry?.laxmi_ads_desc || "NA";
      },
    },

    is_invoice_uploaded: {
      header: "Invoice Uploaded",
      formatter: (_fieldValue, record) => {
        if (typeof record.entry?.is_invoice_uploaded !== "undefined") {
          return record.entry.is_invoice_uploaded ? "Yes" : "No";
        }
        return "NA";
      },
    },

    voucher_code: {
      header: "Voucher Code",
      formatter: (_fieldValue, record) => {
        return record.voucher?.code || "NA";
      },
    },

    is_verified: {
      header: "Verified",
      formatter: (_fieldValue, record) => {
        if (typeof record.is_verified !== "undefined") {
          return record.is_verified ? "Yes" : "No";
        }
        return "NA";
      },
    },

    is_rejected: {
      header: "Rejected",
      formatter: (_fieldValue, record) => {
        if (typeof record.is_rejected !== "undefined") {
          return record.is_rejected ? "Yes" : "No";
        }
        return "NA";
      },
    },

    status: {
      header: "Status",
      formatter: (_fieldValue, record) => {
        if (record.is_verified) return "Verified";
        if (record.is_rejected) return "Rejected";
        return "Pending";
      },
    },

    invoice: {
      header: "Invoice",
      formatter: (_fieldValue, record) => {
        return record.entry?.invoice
          ? azure_object_url + record.entry.invoice
          : "NA";
      },
    },

    batch_date: {
      header: "Batch Date",
      formatter: (_fieldValue, record) => {
        return record.batch?.draw_date
          ? helper.UserDate(record.batch.draw_date, "-04:00")
          : "NA";
      },
    },

    winner_generated_date: {
      header: "Winner Generated Date",
      formatter: (_fieldValue, record) => {
        return record.createdAt
          ? helper.UserDate(record.createdAt, "-04:00")
          : "NA";
      },
    },

    createdAt: {
      header: "Created At",
      formatter: (_fieldValue, record) => {
        return record.entry?.createdAt
          ? helper.UserDate(record.entry.createdAt, "-04:00")
          : "NA";
      },
    },
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Leads</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleNamePural}</Breadcrumb.Item>
        <Breadcrumb.Item>Generate Daily Winners</Breadcrumb.Item>
      </Breadcrumb>

      {/* Voucher Statistics */}
      <Row gutter={15} style={{ marginBottom: 15 }}>
        <Col xs={24} sm={8}>
          <Card loading={voucherStatsLoading}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 16,
                  color: "#666",
                  marginBottom: 8,
                }}
              >
                Total Vouchers
              </div>

              <div style={{ fontSize: 32, fontWeight: 600 }}>
                {voucherStats.totalVouchers}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card loading={voucherStatsLoading}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 16,
                  color: "#666",
                  marginBottom: 8,
                }}
              >
                Used Vouchers
              </div>

              <div style={{ fontSize: 32, fontWeight: 600 }}>
                {voucherStats.usedVouchers}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card loading={voucherStatsLoading}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 16,
                  color: "#666",
                  marginBottom: 8,
                }}
              >
                Remaining Vouchers
              </div>

              <div style={{ fontSize: 32, fontWeight: 600 }}>
                {voucherStats.remainingVouchers}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Generate form card ─────────────────────────────────────────── */}
      <Row gutter={15}>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card title="Generate Daily Winners" bordered={false}>
              <Form layout="inline" form={form} onFinish={handleSubmit}>
                <Form.Item
                  label="Date:"
                  name="date"
                  {...(formError.date && {
                    help: formError.date,
                    validateStatus: "error",
                  })}
                >
                  <DatePicker
                    value={selectedDate ? moment(selectedDate) : null}
                    variant="borderless"
                    onChange={handleDateChange}
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    {/*
                      Generate button is disabled when:
                      - API is loading
                      - A batch already exists for the selected date
                    */}
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={btnloading}
                      disabled={btnloading || batchExists}
                    >
                      Generate
                    </Button>

                    {/* Show informational label when batch already exists */}
                    {batchExists && (
                      <Tag color="blue">Batch already generated.</Tag>
                    )}
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </Col>
      </Row>

      {/* ── Winners table card ─────────────────────────────────────────── */}
      <Row>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card bordered={false}>
              <Table
                rowKey={(record) => record._id}
                columns={columns}
                dataSource={winners}
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showSizeChanger: true,
                  // showQuickJumper: true,
                }}
                exportableProps={{
                  fields,
                  fileName: moduleNamePural + " Daily Winners",
                }}
                searchableProps={{
                  fuzzySearch: false,
                  fuseProps: {
                    useExtendedSearch: true,
                  },
                }}
              />
            </Card>

            {/* ── Winner detail Drawer ───────────────────────────────────── */}
            <Drawer
              title="Winner Details"
              placement="right"
              onClose={handleDrawerClose}
              visible={drawerOpen}
              size="large"
            >
              {drawerWinner && (
                <Descriptions
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                >
                  <Descriptions.Item label="Selection Type">
                    {selectionTypeLabels[drawerWinner.selection_type] ||
                      drawerWinner.selection_type ||
                      "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="First Name">
                    {drawerWinner.entry?.first_name || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Name">
                    {drawerWinner.entry?.last_name || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Contact Number">
                    {drawerWinner.entry?.country_code && drawerWinner.entry?.phone
                      ? `${drawerWinner.entry.country_code} ${drawerWinner.entry.phone}`
                      : drawerWinner.entry?.phone || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email Id">
                    {drawerWinner.entry?.email || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gender">
                    {drawerWinner.entry?.gender || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">
                    {drawerWinner.entry?.date_of_birth
                      ? moment(drawerWinner.entry.date_of_birth).format("D MMMM YYYY")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address">
                    {drawerWinner.entry?.address || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country">
                    {drawerWinner.entry?.country || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="State">
                    {drawerWinner.entry?.state || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="City">
                    {drawerWinner.entry?.city
                      ? _.replace(_.deburr(drawerWinner.entry.city), /'/g, "")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Zip Code">
                    {drawerWinner.entry?.zip || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Which store did you buy this Laxmi product from?">
                    {drawerWinner.entry?.store || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="How did you get to know about this campaign?">
                    {drawerWinner.entry?.campaign_source || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="What is your favorite product from Laxmi?">
                    {drawerWinner.entry?.laxmi_fav_product || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Can you Describe Laxmi Brand in one word?">
                    {drawerWinner.entry?.laxmi_brand_one_word || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Have you seen Laxmi ads before? if yes where?">
                    {drawerWinner.entry?.laxmi_ads_desc || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Invoice">
                    {drawerWinner.entry?.invoice ? (
                      <a
                        href={azure_object_url + drawerWinner.entry.invoice}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Invoice
                      </a>
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Invoice Uploaded">
                    {typeof drawerWinner.entry?.is_invoice_uploaded !==
                      "undefined" ? (
                      drawerWinner.entry.is_invoice_uploaded ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Voucher Code">
                    {drawerWinner.voucher?.code || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Verified">
                    {typeof drawerWinner.is_verified !== "undefined" ? (
                      drawerWinner.is_verified ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rejected">
                    {typeof drawerWinner.is_rejected !== "undefined" ? (
                      drawerWinner.is_rejected ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Batch Date">
                    {drawerWinner.batch?.draw_date
                      ? helper.UserDate(drawerWinner.batch.draw_date, "-04:00")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Winner Generated Date">
                    {drawerWinner.createdAt
                      ? helper.UserDate(drawerWinner.createdAt, "-04:00")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {drawerWinner.entry?.createdAt
                      ? helper.UserDate(drawerWinner.entry.createdAt, "-04:00")
                      : "NA"}
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Drawer>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default DiwaliBonanza2026DailyWinnerGenerate;
